import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Card } from '../components/Card';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import { ApiError } from '../net/client';
import {
  getHeatmap,
  hotspots,
  landmarks,
  mapView,
  marksFor,
  verdictSplit,
  type Heatmap,
  type VerdictFilter,
} from '../net/heatmap';
import { dashboardWindows, getSummary, type ScanSummary, type ScanVerdict } from '../net/reports';
import { useSession } from '../session/useSession';
import { colors, plot, radius, shell, type, verdict } from '../theme';

/**
 * G6, `design/PortalReports.dc.html`.
 *
 * The heat map, Hotspots and Verdict split, over the last 30 days. One
 * `GET /v1/reports/heatmap` with no verdict and one `GET /v1/reports/summary`;
 * the pills filter the loaded cells. Where a mark sits, how it is sized, and
 * what a hotspot row names are D12. The two selects and Export CSV are drawn
 * and do nothing yet: the board draws no menu, and the heat map route takes no
 * product, DEFERRED 1.8.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; heatmap: Heatmap; summary: ScanSummary };

const filters: { key: VerdictFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'genuine', label: 'Genuine' },
  { key: 'suspect', label: 'Suspect' },
];

/** The board's outline of Kenya, verbatim. */
const outline =
  'M180 40 L400 25 L520 90 L610 80 L700 170 L680 300 L560 430 L420 445 L300 400 L200 430 L120 330 L90 210 L140 120 Z';

/** The legend's labels and swatch opacities, verbatim. */
const legend: { verdict: ScanVerdict; label: string; opacity: number }[] = [
  { verdict: 'genuine', label: 'Genuine density', opacity: 0.6 },
  { verdict: 'suspect', label: 'Suspect cluster', opacity: 0.7 },
  { verdict: 'unknown', label: 'Unknown-code cluster', opacity: 0.7 },
];

const splitLabels: Record<ScanVerdict, string> = {
  genuine: 'Genuine',
  suspect: 'Suspect',
  unknown: 'Unknown codes nearby',
};

export function Reports() {
  const { api } = useSession();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [filter, setFilter] = useState<VerdictFilter>('all');

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const range = dashboardWindows(new Date()).current;
    Promise.all([getHeatmap(api, range, signal), getSummary(api, range, signal)]).then(
      ([heatmap, summary]) => setLoad({ status: 'loaded', heatmap, summary }),
      (error: unknown) => {
        if (!signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api]);

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading reports</Waiting> : null}
      <PageHeader
        title="Reports"
        subtitle="Where the genuine product is, and where the suspect scans come from"
        action={
          <button className="vp-outline" type="button" style={exportButton}>
            Export CSV
          </button>
        }
      />
      <div style={{ display: 'flex', gap: 12 }}>
        <Select width={180}>Last 30 days</Select>
        <Select width={240}>All products</Select>
        <div
          role="group"
          aria-label="Verdict"
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}
        >
          {filters.map((f) => (
            <button
              className="vp-outline"
              key={f.key}
              type="button"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
              style={pillStyle(filter === f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {load.status === 'failed' ? (
        <Card title="Reports could not be loaded" body={failureText(load.error)} />
      ) : (
        <div
          aria-busy={load.status === 'loading'}
          style={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr', gap: 16, flexGrow: 1 }}
        >
          <section aria-label="Scan heat map" style={{ ...panel, height: '100%' }}>
            <div
              style={{
                padding: '20px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                height: '100%',
                boxSizing: 'border-box',
              }}
            >
              <h2 style={panelTitle}>Scan heat map · Kenya</h2>
              <HeatMap heatmap={load.status === 'loaded' ? load.heatmap : null} filter={filter} />
              <div style={{ display: 'flex', gap: 20 }}>
                {legend.map((l) => (
                  <div key={l.verdict} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 6,
                        background: verdict[l.verdict].fg,
                        opacity: l.opacity,
                      }}
                    />
                    <div style={{ ...type.caption, color: colors.mutedInk }}>{l.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Hotspots heatmap={load.status === 'loaded' ? load.heatmap : null} />
            <Split summary={load.status === 'loaded' ? load.summary : null} />
          </div>
        </div>
      )}
    </>
  );
}

function HeatMap({ heatmap, filter }: { heatmap: Heatmap | null; filter: VerdictFilter }) {
  const marks = heatmap ? marksFor(heatmap.cells, filter) : [];
  return (
    <div
      style={{
        flexGrow: 1,
        borderRadius: 12,
        background: plot.ground,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${mapView.width} ${mapView.height}`}
        preserveAspectRatio="xMidYMid slice"
        role="img"
        aria-label={`${marks.length} marked cells`}
        style={{ position: 'absolute', inset: 0 }}
      >
        <path d={outline} fill={plot.mapLand} stroke={plot.mapLandStroke} strokeWidth={2} />
        {marks.map((mark) => (
          <g
            key={`${mark.verdict}:${mark.x}:${mark.y}`}
            data-verdict={mark.verdict}
            data-count={mark.count}
          >
            {mark.rings.map((ring) => (
              <circle
                key={ring.r}
                cx={mark.x}
                cy={mark.y}
                r={ring.r}
                fill={verdict[mark.verdict].fg}
                opacity={ring.opacity}
              />
            ))}
          </g>
        ))}
        {landmarks.map((l) => (
          <text
            key={l.name}
            x={l.labelX}
            y={l.labelY}
            fontFamily="Public Sans"
            fontSize={14}
            fontWeight={600}
            fill={colors.navy}
          >
            {l.name}
          </text>
        ))}
      </svg>
    </div>
  );
}

function Hotspots({ heatmap }: { heatmap: Heatmap | null }) {
  const rows = heatmap ? hotspots(heatmap) : [];
  return (
    <section aria-label="Hotspots" style={panel}>
      <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={panelTitle}>Hotspots</h2>
        <ul
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            margin: 0,
            padding: 0,
            listStyle: 'none',
          }}
        >
          {rows.map((row) => (
            <li
              key={`${row.verdict}:${row.place}`}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <span style={type.bodyStrong}>{row.place}</span>
              <span
                style={{
                  display: 'inline-flex',
                  padding: '4px 12px',
                  borderRadius: radius.chip,
                  background: verdict[row.verdict].bg,
                  color: verdict[row.verdict].fg,
                  ...type.captionBold,
                }}
              >
                {`${row.count.toLocaleString('en-US')} ${row.verdict}`}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Split({ summary }: { summary: ScanSummary | null }) {
  const split = summary ? verdictSplit(summary) : null;
  const order: ScanVerdict[] = ['genuine', 'suspect', 'unknown'];
  return (
    <section aria-label="Verdict split" style={panel}>
      <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h2 style={panelTitle}>Verdict split</h2>
        <div
          style={{
            display: 'flex',
            height: 14,
            borderRadius: 7,
            overflow: 'hidden',
            background: split ? undefined : colors.line,
          }}
        >
          {split
            ? order.map((v) => (
                <div
                  key={v}
                  data-verdict={v}
                  style={{ width: `${split[v]}%`, background: verdict[v].fg }}
                />
              ))
            : null}
        </div>
        <dl
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            margin: 0,
            ...type.meta,
            color: colors.mutedInk,
          }}
        >
          {order.map((v) => (
            <div key={v} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <dt>{splitLabels[v]}</dt>
              <dd style={{ margin: 0, fontWeight: 700, color: verdict[v].fg }}>
                {split ? `${split[v].toFixed(1)}%` : ''}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/** Drawn as the board draws it, with no menu: none is drawn, DEFERRED 1.8. */
function Select({ width, children }: { width: number; children: ReactNode }) {
  return (
    <div style={{ ...selectBox, width }}>
      <span>{children}</span>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M6 9 L12 15 L18 9"
          stroke={colors.mutedInk}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

const panel: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

const panelTitle: CSSProperties = { ...type.panelTitle, color: colors.navy, margin: 0 };

const selectBox: CSSProperties = {
  height: 44,
  boxSizing: 'border-box',
  borderRadius: 12,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 14px',
  ...type.body,
  color: colors.navy,
};

/** Selected is navy on white type, the rest the neutral outline: both literal in the board. */
const pillStyle = (selected: boolean): CSSProperties => ({
  padding: '11px 18px',
  borderRadius: 20,
  cursor: 'pointer',
  ...(selected
    ? { background: colors.navy, color: colors.white, border: 'none', ...type.metaBold }
    : {
        background: colors.card,
        color: colors.mutedInk,
        border: `1px solid ${colors.line}`,
        ...type.metaStrong,
      }),
});

const exportButton: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  background: colors.card,
  color: colors.navy,
  border: `2px solid ${colors.navy}`,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};
